import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  IconButton,
  Card,
  CardBody,
  Badge,
  useToast,
  useColorModeValue,
  Alert,
  AlertIcon,
  Spinner,
  SimpleGrid,
  Button,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  FormControl,
  FormLabel
} from '@chakra-ui/react';
import { FiEye, FiClock, FiMapPin, FiDownload, FiClock as FiPending, FiPlay, FiSearch } from 'react-icons/fi';
import PageHeader from './shared/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import ChecklistViewer from './subComponents/ChecklistViewer';
import { getChecklistsByCompanyAndDate, generatePDFSummary, type ChecklistCategory, type PDFGenerationParams } from '../utils/checklistUtils';

interface ChecklistData {
  id: string;
  plateNumber: string;
  status: 'pending' | 'ongoing' | 'completed';
  overallStatus: 'pass' | 'fail' | '';
  inspectionSummary: string;
  updated_at: any;
  updated_by: string;
  completed_by: string;
  companyId: string;
  companyName: string;
  vehicleId?: string;
  created_at?: any;
  wayBillNumber?: string;
  categories?: ChecklistCategory[];
  completedRequired?: number;
  totalRequired?: number;
  signatureDataURL?: string;
}

interface ChecklistMonitoringProps {
  onBack: () => void;
}

const ChecklistMonitoring: React.FC<ChecklistMonitoringProps> = ({ onBack }) => {
  const { selectedCompany } = useAuth();
  const toast = useToast();
  
  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  const [checklists, setChecklists] = useState<ChecklistData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedChecklistId, setSelectedChecklistId] = useState<string | null>(null);
  
  // Search state
  const [createdFrom, setCreatedFrom] = useState<string>('');
  const [createdTo, setCreatedTo] = useState<string>('');
  const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false);
  
  // Modal state
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Initialize date filters to current date
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    setCreatedFrom(today);
    setCreatedTo(today);
  }, []);

  // Load checklists function
  const loadChecklists = async () => {
    if (!selectedCompany?.companyId) return;
    
    try {
      setIsLoading(true);

      // Use the new function with date filtering
      const allChecklists = await getChecklistsByCompanyAndDate(
        selectedCompany.companyId,
        createdFrom || undefined,
        createdTo || undefined
      );

      setChecklists(allChecklists);
      
    } catch (error) {
      console.error('Error loading checklists:', error);
      toast({
        title: 'Error Loading Checklists',
        description: 'Failed to load checklists. Please try refreshing the page.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load when component mounts and dates are initialized (only once)
  useEffect(() => {
    if (selectedCompany?.companyId && createdFrom && createdTo && !hasInitialLoad) {
      loadChecklists();
      setHasInitialLoad(true);
    }
  }, [selectedCompany?.companyId, createdFrom, createdTo, hasInitialLoad]);

  // Handle search button click
  const handleSearch = () => {
    loadChecklists();
  };

  // Handle Enter key press for search
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isLoading) {
      handleSearch();
    }
  };

  const handleViewChecklist = (checklistId: string) => {
    setSelectedChecklistId(checklistId);
    onOpen();
  };

  const handleExportPDF = async (checklistId: string) => {
    // Find the checklist in the current list
    const checklist = checklists.find(c => c.id === checklistId);
    if (!checklist) {
      toast({
        title: 'Checklist Not Found',
        description: 'Could not find the selected checklist.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    try {
      const pdfParams: PDFGenerationParams = {
        plateNumber: checklist.plateNumber,
        companyName: checklist.companyName,
        categories: checklist.categories || [],
        totalRequired: checklist.totalRequired || 0,
        completedRequired: checklist.completedRequired || 0,
        isFullyComplete: checklist.status === 'completed',
        overallStatus: checklist.overallStatus as 'pass' | 'fail' | undefined,
        inspectionSummary: checklist.inspectionSummary,
        signatureDataURL: checklist.signatureDataURL,
        inspectorName: checklist.completed_by,
        updated_at: checklist.updated_at,
        checklistType: 'Pre-Departure Checklist Report'
      };

      const fileName = await generatePDFSummary(pdfParams);

      toast({
        title: 'PDF Exported',
        description: `Checklist exported as ${fileName}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: 'Export Failed',
        description: 'Failed to export checklist as PDF. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'ongoing':
        return 'blue';
      case 'completed':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getOverallStatusColor = (overallStatus: string) => {
    switch (overallStatus) {
      case 'pass':
        return 'green';
      case 'fail':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    
    try {
      // Handle Firestore Timestamp
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
      <PageHeader
        title="Checklist Monitoring"
        subtitle="View pending and ongoing checklists"
        icon="📋"
        onBack={onBack}
      />

      {/* Content */}
      <Container maxW="6xl" py={8} px={4}>
        <VStack spacing={6} align="stretch">
          {/* Search Filters */}
          <Card
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            boxShadow="0 8px 32px rgba(0,0,0,0.12)"
            border="1px solid"
            borderColor="whiteAlpha.500"
          >
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="700" bgGradient="linear(135deg, gray.700, gray.900)" bgClip="text">
                  Search Filters
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.600">Created From</FormLabel>
                    <Input
                      type="date"
                      value={createdFrom}
                      onChange={(e) => setCreatedFrom(e.target.value)}
                      onKeyPress={handleKeyPress}
                      size="md"
                    />
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.600">Created To</FormLabel>
                    <Input
                      type="date"
                      value={createdTo}
                      onChange={(e) => setCreatedTo(e.target.value)}
                      onKeyPress={handleKeyPress}
                      size="md"
                    />
                  </FormControl>
                  
                  <FormControl display="flex" alignItems="end">
                    <Button
                      leftIcon={<FiSearch />}
                      colorScheme="blue"
                      onClick={handleSearch}
                      isLoading={isLoading}
                      loadingText="Searching..."
                      size="md"
                      width="full"
                    >
                      Search Checklists
                    </Button>
                  </FormControl>
                </SimpleGrid>
                
                {/* Search Summary */}
                {(createdFrom || createdTo) && (
                  <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                    <Text fontSize="sm" color="blue.600" fontWeight="medium">
                      {checklists.length} checklist{checklists.length !== 1 ? 's' : ''} found
                    </Text>
                  </Flex>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Overview Boxes */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Pending Checklists Box */}
            <Card 
              bg="linear-gradient(135deg, orange.50 0%, orange.100 100%)" 
              borderColor="orange.200" 
              borderWidth="2px"
              _hover={{ 
                transform: 'translateY(-2px)', 
                shadow: 'xl',
                borderColor: 'orange.300'
              }}
              transition="all 0.3s ease"
              position="relative"
              overflow="hidden"
              {...(checklists.filter(c => c.status === 'completed').length > 0 && {
                animation: 'pulse 2s infinite'
              })}
            >
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                      <HStack spacing={3}>
                        <Box
                          p={3}
                          bg="orange.500"
                          borderRadius="full"
                          color="white"
                          boxShadow="lg"
                          position="relative"
                          _before={{
                            content: '""',
                            position: 'absolute',
                            top: '-2px',
                            left: '-2px',
                            right: '-2px',
                            bottom: '-2px',
                            borderRadius: 'full',
                            background: 'linear-gradient(45deg, orange.400, orange.600)',
                            zIndex: -1,
                          }}
                        >
                          <FiPending size={24} />
                        </Box>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="lg" fontWeight="bold" color="orange.700">
                            Completed Checklists
                          </Text>
                          <Text fontSize="sm" color="orange.600">
                            Awaiting attachment to Delivery
                          </Text>
                        </VStack>
                      </HStack>
                    </VStack>
                    <Box
                      bg="orange.500"
                      color="white"
                      px={4}
                      py={2}
                      borderRadius="full"
                      fontWeight="bold"
                      fontSize="xl"
                      minW="60px"
                      textAlign="center"
                      boxShadow="md"
                      position="relative"
                      _before={{
                        content: '""',
                        position: 'absolute',
                        top: '-1px',
                        left: '-1px',
                        right: '-1px',
                        bottom: '-1px',
                        borderRadius: 'full',
                        background: 'linear-gradient(45deg, orange.400, orange.600)',
                        zIndex: -1,
                      }}
                    >
                      {checklists.filter(c => c.status === 'completed').length}
                    </Box>
                  </HStack>
                  
                  <Box>
                    <Text fontSize="sm" color="orange.600" fontWeight="medium">
                      {checklists.filter(c => c.status === 'completed').length === 0 
                        ? '🎉 No completed checklists at the moment' 
                        : checklists.filter(c => c.status === 'completed').length === 1
                        ? '⚠️ 1 checklist needs attention'
                        : `⚠️ ${checklists.filter(c => c.status === 'completed').length} checklists need attention`
                      }
                    </Text>
                  </Box>
                </VStack>
              </CardBody>
            </Card>

            {/* Ongoing Checklists Box */}
            <Card 
              bg="linear-gradient(135deg, blue.50 0%, blue.100 100%)" 
              borderColor="blue.200" 
              borderWidth="2px"
              _hover={{ 
                transform: 'translateY(-2px)', 
                shadow: 'xl',
                borderColor: 'blue.300'
              }}
              transition="all 0.3s ease"
              position="relative"
              overflow="hidden"
            >
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                      <HStack spacing={3}>
                        <Box
                          p={3}
                          bg="blue.500"
                          borderRadius="full"
                          color="white"
                          boxShadow="lg"
                          position="relative"
                          _before={{
                            content: '""',
                            position: 'absolute',
                            top: '-2px',
                            left: '-2px',
                            right: '-2px',
                            bottom: '-2px',
                            borderRadius: 'full',
                            background: 'linear-gradient(45deg, blue.400, blue.600)',
                            zIndex: -1,
                          }}
                        >
                          <FiPlay size={24} />
                        </Box>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="lg" fontWeight="bold" color="blue.700">
                            Ongoing Checklists
                          </Text>
                          <Text fontSize="sm" color="blue.600">
                            Currently in progress
                          </Text>
                        </VStack>
                      </HStack>
                    </VStack>
                    <Box
                      bg="blue.500"
                      color="white"
                      px={4}
                      py={2}
                      borderRadius="full"
                      fontWeight="bold"
                      fontSize="xl"
                      minW="60px"
                      textAlign="center"
                      boxShadow="md"
                      position="relative"
                      _before={{
                        content: '""',
                        position: 'absolute',
                        top: '-1px',
                        left: '-1px',
                        right: '-1px',
                        bottom: '-1px',
                        borderRadius: 'full',
                        background: 'linear-gradient(45deg, blue.400, blue.600)',
                        zIndex: -1,
                      }}
                    >
                      {checklists.filter(c => c.status === 'ongoing').length}
                    </Box>
                  </HStack>
                  
                  <Box>
                    <Text fontSize="sm" color="blue.600" fontWeight="medium">
                      {checklists.filter(c => c.status === 'ongoing').length === 0 
                        ? '✅ No ongoing checklists at the moment' 
                        : checklists.filter(c => c.status === 'ongoing').length === 1
                        ? '🔄 1 checklist in progress'
                        : `🔄 ${checklists.filter(c => c.status === 'ongoing').length} checklists in progress`
                      }
                    </Text>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Total Summary Card */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <HStack justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    Total Checklist Overview
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Complete view of all pending and ongoing items
                  </Text>
                </VStack>
                <Badge 
                  colorScheme={checklists.length > 0 ? 'green' : 'gray'} 
                  size="lg" 
                  px={4} 
                  py={2} 
                  borderRadius="full"
                  fontSize="md"
                >
                  {checklists.length} Total
                </Badge>
              </HStack>
            </CardBody>
          </Card>

          {/* Loading State */}
          {isLoading && (
            <Card bg={cardBg} borderColor={borderColor}>
              <CardBody>
                <VStack spacing={4} align="center" py={8}>
                  <Spinner size="lg" color="blue.500" />
                  <Text fontSize="lg" fontWeight="semibold">Loading Checklists...</Text>
                  <Text fontSize="sm" color="gray.600">Please wait while we fetch the checklist data</Text>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* No Checklists */}
          {!isLoading && checklists.length === 0 && (
            <Card bg={cardBg} borderColor={borderColor}>
              <CardBody>
                <VStack spacing={4} align="center" py={8}>
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Text>No pending or ongoing checklists found for your company.</Text>
                  </Alert>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Checklist Cards */}
          {!isLoading && checklists.length > 0 && (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {checklists.map((checklist) => (
                <Card key={checklist.id} bg={cardBg} borderColor={borderColor} _hover={{ shadow: 'lg' }}>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      {/* Header */}
                      <Flex justify="space-between" align="start">
                        <VStack align="start" spacing={1}>
                          <Text fontSize="lg" fontWeight="bold" color="blue.600">
                            {checklist.plateNumber}
                          </Text>
                          <HStack spacing={2}>
                            <Badge colorScheme={getStatusColor(checklist.status)} size="sm">
                              {checklist.status.toUpperCase()}
                            </Badge>
                            {checklist.overallStatus && (
                              <Badge colorScheme={getOverallStatusColor(checklist.overallStatus)} size="sm">
                                {checklist.overallStatus.toUpperCase()}
                              </Badge>
                            )}
                          </HStack>
                        </VStack>
                        <IconButton
                          aria-label="Export PDF"
                          icon={<FiDownload />}
                          size="sm"
                          variant="outline"
                          colorScheme="green"
                          onClick={() => handleExportPDF(checklist.id)}
                        />
                      </Flex>

                      {/* Details */}
                      <VStack spacing={3} align="stretch">
                        {/* Inspection Summary */}
                        {checklist.inspectionSummary && (
                          <Box>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                              Inspection Summary
                            </Text>
                            <Text fontSize="sm" noOfLines={2}>
                              {checklist.inspectionSummary}
                            </Text>
                          </Box>
                        )}

                        {/* Waybill Number */}
                        {checklist.wayBillNumber && (
                          <Box>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                              Waybill Number
                            </Text>
                            <Text fontSize="sm">
                              {checklist.wayBillNumber}
                            </Text>
                          </Box>
                        )}

                        {/* Updated Info */}
                        <VStack spacing={2} align="stretch">
                          <HStack spacing={2}>
                            <FiClock />
                            <VStack align="start" spacing={0} flex={1}>
                              <Text fontSize="xs" color="gray.600">Updated</Text>
                              <Text fontSize="xs" fontWeight="medium">
                                {formatDate(checklist.updated_at)}
                              </Text>
                            </VStack>
                          </HStack>
                          
                          <HStack spacing={2}>
                            <FiMapPin />
                            <VStack align="start" spacing={0} flex={1}>
                              <Text fontSize="xs" color="gray.600">Updated By</Text>
                              <Text fontSize="xs" fontWeight="medium">
                                {checklist.updated_by}
                              </Text>
                            </VStack>
                          </HStack>
                        </VStack>
                      </VStack>

                      {/* Actions */}
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        leftIcon={<FiEye />}
                        onClick={() => handleViewChecklist(checklist.id)}
                      >
                        View Details
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Container>

      {/* Checklist Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text fontSize="lg" fontWeight="bold">
              Checklist Details
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ChecklistViewer 
              checklistId={selectedChecklistId} 
              showHeader={false}
              compact={false}
            />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ChecklistMonitoring;
